import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Pause, Play } from "lucide-react";
import { messageBarApi } from "../services/phase1Api";

const BG = { blue: "#1e3a8a", green: "#065f46", amber: "#b45309", red: "#b91c1c", gray: "#374151" };
const PRIORITY_BG = {
  information: "blue", success: "green", reminder: "amber",
  maintenance: "gray", important: "amber", critical: "red",
};

// Standalone / non-customer portals where the bar should never render.
const SKIP_PREFIXES = ["/portal", "/njea", "/test-lab"];

function routeContext(pathname) {
  const p = pathname || "/";
  if (SKIP_PREFIXES.some((s) => p.startsWith(s))) return null;
  if (p === "/diagnostic/login") return null;
  if (p.startsWith("/app")) return "admin";
  if (p === "/diagnostic/login" || p.startsWith("/diagnostic/signup")) return "public";
  if (p.startsWith("/diagnostic")) return "school_users";
  if (p.startsWith("/portfolio/invite")) return "public";
  if (p.startsWith("/portfolio")) return "portfolio_managers";
  if (p.startsWith("/sales")) return "sales_users";
  if (p.startsWith("/employee") || p === "/change-password") return "employees";
  return "public";
}

function detectPilot() {
  try {
    const raw = localStorage.getItem("diag_session");
    if (raw) {
      const s = JSON.parse(raw);
      if (s?.is_pilot || s?.tenant?.is_pilot || s?.account_classification === "Pilot") return true;
    }
  } catch { /* ignore */ }
  return false;
}

const dismissKey = (a) => `mb_dismissed_${a.id}_${a.updated_at || ""}`;

export default function GlobalMessageBar() {
  const location = useLocation();
  const context = routeContext(location.pathname);
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [duration, setDuration] = useState(20);
  const [, force] = useState(0);
  const trackRef = useRef(null);

  // Respect the OS "reduce motion" setting — render a static, readable bar.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReducedMotion(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener("change", apply) : mq.addListener(apply);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", apply) : mq.removeListener(apply); };
  }, []);

  // Fetch active announcements for this route context (poll every 60s).
  useEffect(() => {
    if (!context) { setItems([]); return; }
    let alive = true;
    const load = async () => {
      try {
        const { announcements } = await messageBarApi.getActive(context, detectPilot());
        if (!alive) return;
        const visible = (announcements || []).filter((a) => {
          const store = a.show_again_after_login ? sessionStorage : localStorage;
          return store.getItem(dismissKey(a)) !== "1";
        });
        setItems(visible);
        setIdx(0);
      } catch { if (alive) setItems([]); }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [context, location.pathname]);

  const current = items[idx] || null;

  // Rotate every 8s when multiple active.
  useEffect(() => {
    if (items.length <= 1 || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 8000);
    return () => clearInterval(t);
  }, [items.length, paused]);

  // Measure content width → compute duration for a consistent px/sec speed.
  useEffect(() => {
    if (!current || !trackRef.current) return;
    const seg = trackRef.current.querySelector(".mb-seg");
    const w = seg ? seg.getBoundingClientRect().width : 600;
    const speed = Math.max(20, Number(current.scroll_speed) || 90);
    setDuration(Math.max(6, w / speed));
  }, [current, location.pathname]);

  const colors = useMemo(() => {
    if (!current) return { bg: BG.blue, fg: "#fff" };
    const key = current.bg_color || PRIORITY_BG[current.priority] || "blue";
    return { bg: BG[key] || BG.blue, fg: current.text_color || "#fff" };
  }, [current]);

  if (!context || !current) return null;

  const dismiss = () => {
    const store = current.show_again_after_login ? sessionStorage : localStorage;
    store.setItem(dismissKey(current), "1");
    setItems((prev) => prev.filter((a) => a.id !== current.id));
    setIdx(0);
    force((n) => n + 1);
  };

  const Text = () => (
    <span className="mb-seg text-[13px] sm:text-sm font-medium tracking-tight">{current.message}</span>
  );
  const isStopped = reducedMotion || userPaused;
  const inner = reducedMotion ? (
    <div className="px-4 w-full text-center truncate">
      <Text />
    </div>
  ) : (
    <div
      ref={trackRef}
      className="mb-track"
      style={{ animationDuration: `${duration}s`, animationPlayState: (paused || userPaused) ? "paused" : "running" }}
    >
      <Text />
      <span aria-hidden="true" className="mb-seg text-[13px] sm:text-sm font-medium tracking-tight">{current.message}</span>
    </div>
  );

  return (
    <aside
      data-testid="global-message-bar"
      data-priority={current.priority}
      aria-label="Site announcement"
      className="relative w-full overflow-hidden shadow-sm"
      style={{ background: colors.bg, color: colors.fg, height: 44 }}
      onMouseEnter={() => current.pause_on_hover && setPaused(true)}
      onMouseLeave={() => current.pause_on_hover && setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="flex items-center h-full">
        {current.link_url ? (
          <a
            href={current.link_url}
            target={current.link_target || "_self"}
            rel="noopener noreferrer"
            data-testid="mb-link"
            className="flex-1 min-w-0 overflow-hidden h-full flex items-center underline-offset-2 hover:underline"
          >
            {inner}
          </a>
        ) : (
          <div className="flex-1 min-w-0 overflow-hidden h-full flex items-center">{inner}</div>
        )}
        {!reducedMotion && (
          <button
            onClick={() => setUserPaused((v) => !v)}
            data-testid="mb-pause"
            aria-pressed={isStopped}
            aria-label={isStopped ? "Resume announcement scrolling" : "Pause announcement scrolling"}
            title={isStopped ? "Resume" : "Pause"}
            className="shrink-0 h-full px-2.5 flex items-center opacity-70 hover:opacity-100 transition-opacity"
            style={{ color: colors.fg }}
          >
            {isStopped ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </button>
        )}
        {current.closeable && (
          <button
            onClick={dismiss}
            data-testid="mb-close"
            aria-label="Dismiss announcement"
            className="shrink-0 h-full px-3 flex items-center opacity-80 hover:opacity-100 transition-opacity"
            style={{ color: colors.fg }}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </aside>
  );
}
